import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CommonDataTable from "../../components/CommonDataTable";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { fetchStockTransferOutList } from "../../services/stockTransferOut/stockTransferOut";

const baseColumns = [
  { field: "transferNo", headerName: "Transfer No.", width: 150 },
  { field: "sourceLocation", headerName: "Source", width: 160 },
  { field: "destinationLocation", headerName: "Destination", width: 160 },
  { field: "status", headerName: "Status", width: 110 },
  { field: "totalQty", headerName: "Total Qty", width: 100 },
  { field: "totalAmount", headerName: "Total Amount", width: 120 },
  { field: "expectedDate", headerName: "Expected Date", width: 130 },
  { field: "createdAt", headerName: "Created", width: 110 },
];

export default function StockTransferOutList({ onCreateClick, onView, refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [listLoading, setListLoading] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  /** Sync ref so View always sees the row that opened the menu (avoids stale closure). */
  const menuRowRef = useRef(null);

  const loadData = useCallback(async () => {
    setListLoading(true);
    try {
      const { rows: apiRows, total: apiTotal } = await fetchStockTransferOutList({
        page: page + 1,
        limit: pageSize,
        status: "all",
      });
      setRows(apiRows);
      setTotal(typeof apiTotal === "number" ? apiTotal : apiRows.length);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setListLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const handleDataRefresh = async () => {
    await loadData();
  };

  const actionColumn = useMemo(
    () => ({
      field: "__actions",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            menuRowRef.current = params.row;
            setMenuRow(params.row);
            setMenuAnchorEl(e.currentTarget);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    }),
    []
  );

  return (
    <>
      {listLoading && (
        <p className="text-muted small mb-2 mb-md-0">Loading stock transfers…</p>
      )}
      <CommonDataTable
        id="stock-transfer-out-list"
        allPossibleColumns={[actionColumn, ...baseColumns]}
        initialVisible={["__actions", ...baseColumns.map((c) => c.field)]}
        initialRows={rows}
        rowCount={total}
        page={page}
        pageSize={pageSize}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(0);
        }}
        height={500}
        showCreateButton
        onCreateClick={onCreateClick}
        checkboxSelection
        enableImportButton={false}
        enableExportButton={false}
        forInventory
        onDataRefresh={handleDataRefresh}
        readOnly
      />

      <Menu
        open={Boolean(menuAnchorEl)}
        anchorEl={menuAnchorEl}
        onClose={() => {
          setMenuAnchorEl(null);
          menuRowRef.current = null;
        }}
      >
        <MenuItem
          onClick={() => {
            const row = menuRowRef.current ?? menuRow;
            setMenuAnchorEl(null);
            menuRowRef.current = null;
            if (row) onView?.(row);
          }}
        >
          <i className="bi bi-eye me-2" />
          View
        </MenuItem>
      </Menu>
    </>
  );
}
